// src/pages/landing-builder/shared.jsx
import { Layout, Sparkles, Target, Zap, Shield, Heart, Star, Users,
  BookOpen, Mail, Phone, MapPin, Play, Check, List, CreditCard, HelpCircle, X } from 'lucide-react';

// ==================== TEMPLATE TYPES ====================
export const TEMPLATE_TYPES = {
  standard: {
    name: 'Standard Page',
    description: 'Drag-and-drop builder with sections — hero, features, pricing, video & more.',
    icon: Layout,
    badge: 'bg-blue-500',
  },
  optin: {
    name: '2-Step Funnel',
    description: 'User picks an option (Step 1) → sees countdown + CTA (Step 2) + urgency popup.',
    icon: Sparkles,
    badge: 'bg-purple-500',
  },
  quiz: {
    name: 'Quiz Funnel',
    description: 'Single page with multiple questions. CTA unlocks only when every question is answered.',
    icon: Target,
    badge: 'bg-orange-500',
  },
};

// ==================== TEXT SIZES ====================
export const TEXT_SIZES = {
  h1: { label: 'H1 - Title', css: '2.5rem', weight: '700' },
  h2: { label: 'H2 - Heading', css: '2rem', weight: '700' },
  h3: { label: 'H3 - Subheading', css: '1.5rem', weight: '600' },
  h4: { label: 'H4 - Small Heading', css: '1.25rem', weight: '600' },
  p: { label: 'Paragraph', css: '1rem', weight: '400' },
  small: { label: 'Small Text', css: '0.875rem', weight: '400' },
  custom: { label: 'Custom Size', css: '', weight: '400' },
};

// ==================== ICON MAP ====================
export const ICON_MAP = {
  zap: Zap, shield: Shield, heart: Heart, target: Target, star: Star,
  users: Users, book: BookOpen, mail: Mail, phone: Phone, map: MapPin,
  play: Play, check: Check, list: List, credit: CreditCard, help: HelpCircle,
};

// ==================== HELPERS ====================
export const getTextStyle = (sizeKey, customSize, customColor) => {
  const size = TEXT_SIZES[sizeKey] || TEXT_SIZES.p;
  return {
    fontSize: sizeKey === 'custom' && customSize ? `${customSize}px` : size.css,
    fontWeight: size.weight,
    color: customColor || undefined,
  };
};

// ==================== COLOR PICKER ====================
export const ColorPicker = ({ value, onChange, label, placeholder = 'Use theme color' }) => (
  <div>
    {label && <label className="block text-sm font-medium mb-1">{label}</label>}
    <div className="flex gap-2">
      <input
        type="color"
        value={value || '#000000'}
        onChange={e => onChange(e.target.value)}
        className="w-10 h-10 rounded border cursor-pointer"
      />
      <input
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="flex-1 px-2 text-sm border border-border rounded bg-card text-foreground"
        placeholder={placeholder}
      />
      {value && (
        <button onClick={() => onChange('')} className="p-2 text-muted-foreground/70 hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>
);

// ==================== TEXT SIZE EDITOR ====================
export const TextSizeEditor = ({ value, customValue, onChange, onCustomChange, label }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <select
      value={value || 'p'}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground"
    >
      {Object.entries(TEXT_SIZES).map(([key, { label: l }]) => (
        <option key={key} value={key}>{l}</option>
      ))}
    </select>
    {value === 'custom' && (
      <div className="flex gap-2 mt-2">
        <input
          type="number"
          value={customValue || '16'}
          onChange={e => onCustomChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground"
          min="8" max="120"
        />
        <span className="py-2 text-sm text-muted-foreground">px</span>
      </div>
    )}
  </div>
);

// ==================== SYSTEM TEMPLATES ====================
// Only real, working templates. Grouped strictly by templateType.
export const SYSTEM_TEMPLATES = [

  // ── STANDARD ─────────────────────────────────────────────────────────────────
  {
    id: 'std-basic',
    name: 'Basic Landing Page',
    description: 'Hero + Features + CTA — clean starting point',
    templateType: 'standard',
    page_config: {
      templateType: 'standard',
      theme: { primaryColor: '#10B981', backgroundColor: '#ffffff', textColor: '#1f2937', fontFamily: 'Inter, sans-serif' },
      sections: [
        { id: 'hero-1', type: 'hero', config: { headline: 'Your Compelling Headline', headlineSize: 'h1', subheadline: 'A brief description that explains your offer', subheadlineSize: 'p', alignment: 'center', padding: 'large', backgroundType: 'color', backgroundColor: '#ffffff' } },
        { id: 'features-1', type: 'features', config: { headline: 'Why Choose Us', headlineSize: 'h2', columns: 3, items: [{ icon: 'zap', title: 'Fast', description: 'Lightning speed' }, { icon: 'shield', title: 'Secure', description: 'Bank-level security' }, { icon: 'heart', title: 'Simple', description: 'Easy to use' }] } },
        { id: 'cta-1', type: 'cta', config: { text: 'Get Started Now', style: 'solid', size: 'large', alignment: 'center' } },
      ],
      footer: { enabled: true, customText: '© 2024 Your Company' },
    },
  },
  {
    id: 'std-sales',
    name: 'Sales Page',
    description: 'Hero + Testimonials + Pricing + FAQ',
    templateType: 'standard',
    page_config: {
      templateType: 'standard',
      theme: { primaryColor: '#7C3AED', backgroundColor: '#ffffff', textColor: '#1f2937', fontFamily: 'Inter, sans-serif' },
      sections: [
        { id: 'hero-1', type: 'hero', config: { headline: 'Transform Your Results Today', headlineSize: 'h1', subheadline: 'Join thousands who have already made the switch', subheadlineSize: 'p', alignment: 'center', padding: 'large', backgroundType: 'color', backgroundColor: '#f5f3ff' } },
        { id: 'features-1', type: 'features', config: { headline: 'Everything You Need', headlineSize: 'h2', columns: 3, items: [{ icon: 'zap', title: 'Instant Results', description: 'See changes immediately' }, { icon: 'shield', title: 'Guaranteed', description: '30-day money back' }, { icon: 'star', title: 'Award Winning', description: 'Industry recognised' }] } },
        { id: 'testimonials-1', type: 'testimonials', config: { headline: 'What Our Customers Say', headlineSize: 'h2', items: [{ quote: 'This changed everything for me!', author: 'Sarah J.', role: 'Business Owner' }, { quote: 'Best investment I ever made.', author: 'Mike R.', role: 'Freelancer' }] } },
        { id: 'pricing-1', type: 'pricing', config: { headline: 'Simple Pricing', headlineSize: 'h2', plans: [{ name: 'Starter', price: '29', period: 'month', features: ['Core features', 'Email support'], cta: 'Start Free', highlighted: false }, { name: 'Pro', price: '79', period: 'month', features: ['All features', 'Priority support', 'API access'], cta: 'Go Pro', highlighted: true }] } },
        { id: 'faq-1', type: 'faq', config: { headline: 'Common Questions', headlineSize: 'h2', items: [{ question: 'How does it work?', answer: 'Simply sign up and start using immediately.' }, { question: 'Can I cancel anytime?', answer: 'Yes, no contracts.' }] } },
        { id: 'cta-2', type: 'cta', config: { text: 'Start Your Free Trial', style: 'solid', size: 'large', alignment: 'center' } },
      ],
      footer: { enabled: true, customText: '© 2024 Your Company', showPrivacyLink: true, showTermsLink: true },
    },
  },

  // ── 2-STEP FUNNEL ─────────────────────────────────────────────────────────────
  // Based exactly on: https://you.incomeislife.com/start/
  {
    id: 'optin-grant',
    name: 'Grant / Loan Access',
    description: 'Like incomeislife.com — pick amount → countdown → CTA → urgency popup',
    templateType: 'optin',
    page_config: {
      templateType: 'optin',
      theme: { primaryColor: '#008751', backgroundColor: '#7cb342', textColor: '#1f2937', fontFamily: 'Segoe UI, sans-serif' },
      optin: {
        flowType: '2step',
        logoUrl: '',
        title: 'How much do you need?',
        subtitle: '',
        titleSize: '22px',
        titleColor: '#008751',
        subtitleColor: '#333',
        options: [
          { text: '₦50,000 to ₦500,000', destinationUrl: '' },
          { text: '₦500,000 to ₦1,000,000', destinationUrl: '' },
        ],
        optionTextColor: '#006A41',
        ctaText: '👉 Continue',
        ctaDestinationUrl: '',
        ctaTextColor: '#ffffff',
        isTestCta: false,
        countdownEnabled: true,
        countdownDuration: 30,
        countdownStopAt: 2,
        popupEnabled: true,
        popupTitle: '⏰ Hurry up, time will soon run out!',
        popupMessage: 'Accept now to continue your application.',
        popupCtaText: '👉 Accept Now',
        popupDelay: 8,
        popupIsTestCta: false,
        socialProofEnabled: true,
        socialProofMessage: 'has been granted.',
        socialProofNames: ['Chinedu', 'Amina', 'Blessing', 'Emeka', 'Tosin', 'Ada', 'Kabir', 'Fatima'],
        socialProofLocations: ['Lagos', 'Abuja', 'Kano', 'Port Harcourt', 'Ibadan', 'Enugu', 'Kaduna', 'Jos'],
        socialProofInterval: 8,
        showTermsLink: true,
        termsUrl: '/terms',
        termsColor: '#888888',
        backgroundColor: '#7cb342',
        containerBgColor: '#ffffff',
      },
      footer: { enabled: false },
    },
  },
  {
    id: 'optin-webinar',
    name: 'Webinar Registration',
    description: 'Pick session time → confirmation + countdown → reserve spot',
    templateType: 'optin',
    page_config: {
      templateType: 'optin',
      theme: { primaryColor: '#EF4444', backgroundColor: '#1e3a5f', textColor: '#ffffff', fontFamily: 'Segoe UI, sans-serif' },
      optin: {
        flowType: '2step',
        logoUrl: '',
        title: 'When can you attend the FREE Training?',
        subtitle: 'Learn the exact system that generated $1M in 90 days',
        titleSize: '20px',
        titleColor: '#EF4444',
        subtitleColor: '#aaaaaa',
        options: [
          { text: 'This Week', destinationUrl: '' },
          { text: 'Next Week', destinationUrl: '' },
          { text: 'Flexible — show me all dates', destinationUrl: '' },
        ],
        optionTextColor: '',
        ctaText: '👉 Reserve My Spot Now',
        ctaDestinationUrl: '',
        ctaTextColor: '#ffffff',
        isTestCta: false,
        countdownEnabled: true,
        countdownDuration: 45,
        countdownStopAt: 3,
        popupEnabled: true,
        popupTitle: '⚠️ Only 12 spots left!',
        popupMessage: "Spots are filling fast. Confirm your seat before it's gone.",
        popupCtaText: '✅ Yes, Save My Spot',
        popupDelay: 10,
        popupIsTestCta: false,
        socialProofEnabled: true,
        socialProofMessage: 'just registered',
        socialProofNames: ['David', 'Emma', 'James', 'Sophia', 'Liam'],
        socialProofLocations: ['London', 'Manchester', 'Lagos', 'New York', 'Toronto'],
        socialProofInterval: 7,
        showTermsLink: true,
        termsUrl: '/terms',
        termsColor: '#aaaaaa',
        backgroundColor: '#1e3a5f',
        containerBgColor: '#ffffff',
      },
      footer: { enabled: false },
    },
  },
  {
    id: 'optin-1step',
    name: 'Single CTA Page',
    description: 'One button page — no option selection, direct CTA',
    templateType: 'optin',
    page_config: {
      templateType: 'optin',
      theme: { primaryColor: '#F59E0B', backgroundColor: '#1a1a2e', textColor: '#ffffff', fontFamily: 'Segoe UI, sans-serif' },
      optin: {
        flowType: '1step',
        logoUrl: '',
        title: 'Get Instant Access — FREE',
        subtitle: 'Join 5,000+ members already inside',
        titleSize: '22px',
        titleColor: '#F59E0B',
        subtitleColor: '#cccccc',
        options: [],
        optionTextColor: '',
        ctaText: '👉 Yes! Give Me Access Now',
        ctaDestinationUrl: '',
        ctaTextColor: '#ffffff',
        isTestCta: false,
        countdownEnabled: false,
        popupEnabled: false,
        socialProofEnabled: true,
        socialProofMessage: 'just joined',
        socialProofNames: ['Alex', 'Maria', 'John', 'Priya', 'Samuel'],
        socialProofLocations: ['Lagos', 'London', 'New York', 'Mumbai', 'Accra'],
        socialProofInterval: 6,
        showTermsLink: true,
        termsUrl: '/terms',
        termsColor: '#888888',
        backgroundColor: '#1a1a2e',
        containerBgColor: '#2d2d44',
      },
      footer: { enabled: false },
    },
  },

  // ── QUIZ FUNNEL ───────────────────────────────────────────────────────────────
  {
    id: 'quiz-basic',
    name: 'Basic Quiz',
    description: 'Pick answers → CTA unlocks — like the second HTML example',
    templateType: 'quiz',
    page_config: {
      templateType: 'quiz',
      theme: { primaryColor: '#008751', backgroundColor: '#7cb342', textColor: '#1f2937', fontFamily: 'Segoe UI, sans-serif' },
      quiz: {
        logoUrl: '',
        title: 'Answer to Continue',
        subtitle: 'Please select an option for each question below',
        titleSize: '22px',
        titleColor: '#008751',
        subtitleColor: '#555',
        questions: [
          { label: 'What is your main goal?', options: ['Grow my income', 'Save more money', 'Start a business', 'Invest wisely'], layout: 'vertical' },
          { label: 'How quickly do you want results?', options: ['Within 30 days', 'Within 3 months', 'Within 6 months'], layout: 'vertical' },
        ],
        questionColor: '#333',
        optionTextColor: '#333',
        showProgress: true,
        ctaText: '👉 Continue',
        ctaDestinationUrl: '',
        ctaTextColor: '#ffffff',
        ctaLockedMessage: 'Please answer all questions above to continue',
        isTestCta: false,
        socialProofEnabled: true,
        socialProofMessage: 'just completed this quiz',
        socialProofNames: ['Emeka', 'Fatima', 'Chidi', 'Ngozi'],
        socialProofLocations: ['Lagos', 'Abuja', 'Port Harcourt', 'Kano'],
        socialProofInterval: 9,
        showTermsLink: true,
        termsUrl: '/terms',
        termsColor: '#ffffff',
        backgroundColor: '#7cb342',
        containerBgColor: '#ffffff',
      },
      footer: { enabled: false },
    },
  },
  {
    id: 'quiz-assessment',
    name: 'Business Assessment',
    description: 'Rate your business across 3 areas to see your result',
    templateType: 'quiz',
    page_config: {
      templateType: 'quiz',
      theme: { primaryColor: '#8B5CF6', backgroundColor: '#f5f3ff', textColor: '#1f2937', fontFamily: 'Inter, sans-serif' },
      quiz: {
        logoUrl: '',
        title: 'Rate Your Business',
        subtitle: 'Answer all 3 questions to get your personalised score',
        titleSize: '22px',
        titleColor: '#8B5CF6',
        subtitleColor: '#666',
        questions: [
          { label: 'How would you rate your current marketing?', options: ['Needs work', 'Getting there', 'Crushing it'], layout: 'horizontal' },
          { label: 'How consistent is your monthly revenue?', options: ['Very unpredictable', 'Somewhat stable', 'Very consistent'], layout: 'horizontal' },
          { label: 'How automated are your business processes?', options: ['Mostly manual', 'Some automation', 'Highly automated'], layout: 'horizontal' },
        ],
        questionColor: '#333',
        optionTextColor: '#333',
        showProgress: true,
        ctaText: '👉 See My Results',
        ctaDestinationUrl: '',
        ctaTextColor: '#ffffff',
        ctaLockedMessage: 'Complete all 3 questions to see your score',
        isTestCta: false,
        socialProofEnabled: false,
        showTermsLink: false,
        backgroundColor: '#f5f3ff',
        containerBgColor: '#ffffff',
      },
      footer: { enabled: false },
    },
  },
  {
    id: 'quiz-choice',
    name: 'Choose Your Amount',
    description: 'Single question — select amount to activate the CTA button',
    templateType: 'quiz',
    page_config: {
      templateType: 'quiz',
      theme: { primaryColor: '#017c45', backgroundColor: '#f2f2f2', textColor: '#222', fontFamily: 'Arial, sans-serif' },
      quiz: {
        logoUrl: '',
        title: 'Choose Amount You Need',
        subtitle: '',
        titleSize: '22px',
        titleColor: '#36454F',
        subtitleColor: '#666',
        questions: [
          { label: '', options: ['100,000 Naira', '200,000 Naira'], layout: 'vertical' },
        ],
        questionColor: '#333',
        optionTextColor: '#017c45',
        showProgress: false,
        ctaText: 'APPLY NOW',
        ctaDestinationUrl: '',
        ctaTextColor: '#ffffff',
        ctaLockedMessage: '',
        isTestCta: false,
        socialProofEnabled: false,
        showTermsLink: false,
        backgroundColor: '#f2f2f2',
        containerBgColor: '#ffffff',
      },
      footer: { enabled: false },
    },
  },
];
